import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Trophy, Users, BookOpen, TrendingUp, Building2, Award, ClipboardList,
  Shield, ArrowRight, BarChart3, Languages, FileText, Activity, Database,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const ROLE_COLORS = ['#14532D', '#B45309', '#7C3AED', '#0891B2', '#DC2626'];

export default function AdminDashboard() {
  const { t, lang, user } = useApp();
  const [data, setData] = useState(null);
  const [advanced, setAdvanced] = useState(null);
  const [users, setUsers] = useState([]);
  const [centers, setCenters] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get('/analytics/overview').then(r => setData(r.data));
    api.get('/admin/analytics/advanced').then(r => setAdvanced(r.data)).catch(() => {});
    api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/admin/centers').then(r => setCenters(r.data)).catch(() => {});
    api.get('/admin/audit-log?limit=8').then(r => setAudit(r.data)).catch(() => {});
  }, []);

  if (!data) return <main id="main" className="p-8" data-testid="admin-loading">{t('loading')}</main>;

  // Users by role for pie chart
  const usersByRole = ['admin', 'teacher', 'parent', 'student'].map(role => ({
    role,
    label: lang === 'ar' ? ({ admin: 'مسؤول', teacher: 'معلم', parent: 'ولي', student: 'طالب' })[role] : role,
    count: users.filter(u => u.role === role).length,
  })).filter(x => x.count > 0);

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-amber-50 to-emerald-50 p-6 sm:p-8 mb-8" data-testid="admin-hero">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-rose-600 text-white grid place-items-center shadow-lg">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-rose-700 mb-1">{lang === 'ar' ? 'مركز قيادة النظام' : 'System Command Center'}</div>
              <h1 className="text-2xl sm:text-3xl font-black">{user?.full_name}</h1>
              <p className="text-sm text-muted-foreground mt-1">{lang === 'ar' ? 'إدارة شاملة للمستخدمين، المراكز، التحليلات، والسجل.' : 'Full control of users, centers, analytics and audit logs.'}</p>
            </div>
          </div>
          <Badge className="bg-rose-600 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5" data-testid="admin-role-badge">
            {lang === 'ar' ? 'صلاحيات إدارية كاملة' : 'Full admin access'}
          </Badge>
        </div>
      </section>

      {/* KPIs row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <KPI icon={Users} label={lang === 'ar' ? 'مستخدمون' : 'Users'} value={data.total_users} color="text-rose-600" testid="kpi-users" />
        <KPI icon={Users} label={lang === 'ar' ? 'طلاب' : 'Students'} value={data.total_students} color="text-primary" testid="kpi-students" />
        <KPI icon={Building2} label={lang === 'ar' ? 'مراكز' : 'Centers'} value={advanced?.totals?.centers ?? '—'} color="text-cyan-700" testid="kpi-centers" />
        <KPI icon={ClipboardList} label={lang === 'ar' ? 'خطط فردية' : 'IEPs'} value={data.total_ieps} color="text-amber-600" testid="kpi-ieps" />
        <KPI icon={Award} label={lang === 'ar' ? 'شهادات' : 'Certificates'} value={advanced?.totals?.certificates ?? 0} color="text-emerald-600" testid="kpi-certs" />
        <KPI icon={BookOpen} label={lang === 'ar' ? 'أهداف منهج' : 'Goals'} value={data.total_goals} color="text-purple-700" testid="kpi-goals" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick admin links */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-rose-600" />{lang === 'ar' ? 'إجراءات الإدارة' : 'Admin actions'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              <AdminTile icon={Users} title={lang === 'ar' ? 'إدارة المستخدمين' : 'Users management'} subtitle={lang === 'ar' ? `${users.length} مستخدم — تغيير الأدوار والصلاحيات` : `${users.length} users — change roles & access`} to="/admin/users" testid="goto-users" />
              <AdminTile icon={Building2} title={lang === 'ar' ? 'إدارة المراكز' : 'Centers / tenants'} subtitle={lang === 'ar' ? `${centers.length} مركز — multi-tenant` : `${centers.length} centers — multi-tenant`} to="/admin/centers" testid="goto-centers" />
              <AdminTile icon={BarChart3} title={lang === 'ar' ? 'التحليلات المتقدّمة' : 'Advanced analytics'} subtitle={lang === 'ar' ? 'اتجاهات، خرائط حرارية، مقارنات بين المراكز' : 'Trends, heatmaps, center comparisons'} to="/admin/analytics" testid="goto-analytics" />
              <AdminTile icon={FileText} title={lang === 'ar' ? 'سجلّ النظام' : 'Audit log'} subtitle={lang === 'ar' ? 'تتبّع كل إجراء إداري' : 'Track every admin action'} to="/admin/audit" testid="goto-audit" />
              <AdminTile icon={Languages} title={lang === 'ar' ? 'الترجمات' : 'Translations'} subtitle={lang === 'ar' ? 'مزامنة المنهج بين العربية والإنجليزية' : 'Sync AR ↔ EN curriculum'} to="/admin/translations" testid="goto-translations" />
              <AdminTile icon={Database} title={lang === 'ar' ? 'إعادة البذر' : 'Re-seed demo'} subtitle={lang === 'ar' ? 'حسابات تجريبية و طالب تجريبي' : 'Demo users + demo student'} onClick={async () => { try { await api.post('/admin/seed-demo-users'); window.location.reload(); } catch (e) {} }} testid="seed-btn" />
            </div>
          </CardContent>
        </Card>

        {/* Users by role distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Users className="w-5 h-5 text-rose-600" />{lang === 'ar' ? 'توزيع الأدوار' : 'User roles'}</CardTitle>
          </CardHeader>
          <CardContent>
            {usersByRole.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">{lang === 'ar' ? 'لا بيانات' : 'No data'}</p>
            ) : (
              <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={usersByRole} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={70} innerRadius={36} paddingAngle={2}>
                      {usersByRole.map((_, i) => <Cell key={i} fill={ROLE_COLORS[i % ROLE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* By disability */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />{t('students_by_disability')}</CardTitle></CardHeader>
          <CardContent>
            {data.students_by_disability.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">{t('no_results')}</p>
            ) : (
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.students_by_disability}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={70} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#14532D" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Trophy className="w-5 h-5 text-accent" />{t('leaderboard')}</CardTitle>
          </CardHeader>
          <CardContent>
            {data.leaderboard.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">{t('no_results')}</p>
            ) : (
              <ol className="space-y-2">
                {data.leaderboard.slice(0, 6).map((s, i) => (
                  <li key={s.id} className="flex items-center justify-between border border-border rounded p-2" data-testid={`leader-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`font-black w-6 text-center ${i === 0 ? 'text-amber-600' : i === 1 ? 'text-stone-500' : i === 2 ? 'text-orange-700' : 'text-primary'}`}>{i + 1}</span>
                      <span className="font-medium text-sm line-clamp-1">{s.full_name_ar}</span>
                    </div>
                    <Badge variant="secondary">{s.points || 0}</Badge>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Audit log preview */}
      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><Activity className="w-5 h-5 text-rose-600" />{lang === 'ar' ? 'آخر الإجراءات' : 'Recent activity'}</CardTitle>
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/audit">{lang === 'ar' ? 'الكل' : 'View all'}<ArrowRight className={`w-3.5 h-3.5 ms-1 ${lang === 'ar' ? 'rotate-180' : ''}`} /></Link>
          </Button>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">{lang === 'ar' ? 'لا توجد إجراءات بعد' : 'No actions yet'}</p>
          ) : (
            <ul className="space-y-1 text-sm" data-testid="audit-preview">
              {audit.map(a => (
                <li key={a.id} className="flex items-center justify-between border border-border rounded p-2 bg-white">
                  <div className="flex items-center gap-2 min-w-0">
                    <Activity className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                    <span className="font-mono text-[11px] bg-rose-50 text-rose-700 px-2 py-0.5 rounded">{a.action}</span>
                    <span className="text-muted-foreground text-xs line-clamp-1">{a.actor_email}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function KPI({ icon: Icon, label, value, color = 'text-primary', testid }) {
  return (
    <Card className="card-elevate" data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icon className={`w-4 h-4 ${color}`} aria-hidden="true" />
        </div>
        <div className={`text-2xl font-black ${color} stat-number`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function AdminTile({ icon: Icon, title, subtitle, to, onClick, testid }) {
  const Wrapper = to ? Link : 'button';
  const props = to ? { to } : { onClick, type: 'button' };
  return (
    <Wrapper {...props} data-testid={testid} className="border-2 border-border rounded-xl p-4 card-elevate bg-white hover:border-rose-300 hover:bg-rose-50/30 transition-all text-start w-full block">
      <div className="flex items-start justify-between mb-2">
        <Icon className="w-6 h-6 text-rose-600" />
        <ArrowRight className="w-4 h-4 text-muted-foreground rtl:rotate-180" />
      </div>
      <div className="font-bold text-sm mb-0.5">{title}</div>
      <div className="text-[11px] text-muted-foreground line-clamp-2">{subtitle}</div>
    </Wrapper>
  );
}
